/**
 * Telefone/celular/WhatsApp — libphonenumber-js (default BR).
 */
import {
  parsePhoneNumberFromString,
  AsYouType,
  isValidPhoneNumber as libIsValidPhoneNumber,
} from "libphonenumber-js/max";

const SENTINEL_RE = /^(grupo:|.*@g\.us$)/i;

export function isPhoneSentinel(v) {
  return SENTINEL_RE.test(String(v ?? "").trim());
}

export function phoneDigitsOnly(v) {
  return String(v ?? "").replace(/\D+/g, "");
}

/**
 * Valor exibido no input a partir do que veio da API (E.164, máscara BR, etc.).
 */
export function formatStoredPhoneForInput(raw, defaultCountry = "BR") {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (isPhoneSentinel(s)) return s;

  let pn = parsePhoneNumberFromString(s, defaultCountry);
  if (!pn || !pn.isValid()) {
    const d = phoneDigitsOnly(s);
    if (d) pn = parsePhoneNumberFromString(`+${d}`, defaultCountry);
  }
  if (!pn || !pn.isValid()) pn = parsePhoneNumberFromString(s);
  if (pn && pn.isValid()) {
    return pn.country === defaultCountry ? pn.formatNational() : pn.formatInternational();
  }
  return s;
}

/**
 * E.164 sem '+' para enviar à API; sentinelas intactas.
 */
export function toE164Digits(input, defaultCountry = "BR") {
  if (input == null || input === "") return "";
  const raw = String(input).trim();
  if (isPhoneSentinel(raw)) return raw;

  let pn = parsePhoneNumberFromString(raw, defaultCountry);
  if (!pn || !pn.isValid()) {
    const d = phoneDigitsOnly(raw);
    if (d) pn = parsePhoneNumberFromString(`+${d}`, defaultCountry);
  }
  if (!pn || !pn.isValid()) pn = parsePhoneNumberFromString(raw);
  if (pn && pn.isValid()) return pn.number.replace(/^\+/, "");

  const digits = phoneDigitsOnly(raw);
  if (!digits) return "";
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  return digits;
}

export function formatPhoneDisplay(input, defaultCountry = "BR") {
  if (input == null || input === "") return "";
  const raw = String(input).trim();
  if (isPhoneSentinel(raw)) return raw;

  let pn = parsePhoneNumberFromString(raw, defaultCountry);
  if (!pn || !pn.isValid()) {
    const d = phoneDigitsOnly(raw);
    if (d) pn = parsePhoneNumberFromString(`+${d}`, defaultCountry);
  }
  if (!pn || !pn.isValid()) return raw;
  return pn.country === defaultCountry ? pn.formatNational() : pn.formatInternational();
}

export function isValidPhoneField(value, defaultCountry = "BR") {
  if (value == null || String(value).trim() === "") return true;
  if (isPhoneSentinel(String(value))) return true;
  return libIsValidPhoneNumber(String(value), defaultCountry) || libIsValidPhoneNumber(`+${phoneDigitsOnly(value)}`, defaultCountry);
}

export function getPhoneValidationMessage(value, defaultCountry = "BR") {
  if (value == null || String(value).trim() === "") return "";
  if (isPhoneSentinel(String(value))) return "";
  if (isValidPhoneField(value, defaultCountry)) return "";
  return "Número de telefone inválido. Inclua DDD (Brasil) ou + e código do país.";
}

export function phonesEqual(a, b, defaultCountry = "BR") {
  const da = toE164Digits(a, defaultCountry);
  const db = toE164Digits(b, defaultCountry);
  if (!da || !db) return false;
  if (isPhoneSentinel(da) || isPhoneSentinel(db)) return String(a).trim() === String(b).trim();
  return da === db;
}

/** Match tolerante a cadastros legados (sem DDI ou 9). */
export function phonesLikelyMatch(peerDigitsOrRaw, clientRow, defaultCountry = "BR") {
  const peer = toE164Digits(peerDigitsOrRaw, defaultCountry);
  const a = phoneDigitsOnly(clientRow?.celular || clientRow?.telefone || clientRow?.phone || "");
  const w = phoneDigitsOnly(peer || peerDigitsOrRaw);
  if (!a || !w) return false;
  const clientRaw = clientRow?.celular || clientRow?.telefone || clientRow?.phone || "";
  if (phonesEqual(peerDigitsOrRaw, clientRaw, defaultCountry)) return true;
  if (a.length >= 8 && w.length >= 8 && (a.slice(-11) === w.slice(-11) || a.slice(-10) === w.slice(-10))) return true;
  return a.endsWith(w.slice(-10)) || w.endsWith(a.slice(-10));
}

/**
 * Formata texto digitado/colar (AsYouType novo a cada valor completo).
 */
export function formatPhoneAsYouType(input, defaultCountry = "BR") {
  const s = String(input ?? "");
  if (!s.trim()) return "";
  if (isPhoneSentinel(s)) return s;
  const useIntl = s.trim().startsWith("+");
  const ayt = new AsYouType(useIntl ? undefined : defaultCountry);
  return ayt.input(s);
}

export { AsYouType, libIsValidPhoneNumber as isValidPhoneNumber };
